import { Util } from "pdfjs-dist";

/**
 * Build editable text regions from a PDF page, aligned to the CSS viewport
 * (same size as the on-screen canvas). Items are merged into lines so editing
 * is useful instead of per-glyph/fragment hits.
 */
export async function extractEditableLines(pdfPage, cssViewport) {
  const content = await pdfPage.getTextContent({
    includeMarkedContent: false,
    disableCombineTextItems: false,
  });
  const styles = content.styles || {};
  const pageW = cssViewport.width;
  const pageH = cssViewport.height;

  const raw = [];
  for (let i = 0; i < content.items.length; i++) {
    const item = content.items[i];
    if (!item || item.str == null) continue;
    const str = String(item.str);
    if (!str.length) continue;

    const geom = measureTextItem(item, cssViewport, styles);
    if (!geom || geom.wPx < 0.5 || geom.hPx < 1) continue;

    raw.push({
      idx: i,
      str,
      left: geom.left,
      top: geom.top,
      width: geom.wPx,
      height: geom.hPx,
      fontSize: geom.fontSize,
      hasEOL: !!item.hasEOL,
    });
  }

  if (!raw.length) return [];

  const lines = mergeItemsIntoLines(raw);
  return lines.map((line, i) => {
    const padX = Math.max(1.5, line.height * 0.04);
    const padY = Math.max(1, line.height * 0.08);
    const left = clamp(line.left - padX, 0, pageW);
    const top = clamp(line.top - padY, 0, pageH);
    const right = clamp(line.right + padX, 0, pageW);
    const bottom = clamp(line.bottom + padY, 0, pageH);
    const w = Math.max(right - left, 4);
    const h = Math.max(bottom - top, 4);

    return {
      id: `line-${i}-${Math.round(left)}-${Math.round(top)}`,
      str: line.text,
      x: left / pageW,
      y: top / pageH,
      w: w / pageW,
      h: h / pageH,
      // CSS px at current zoom — convert to PDF points with / zoom when exporting
      fontSizeCss: line.fontSize,
      left,
      top,
      width: w,
      height: h,
    };
  });
}

function measureTextItem(item, viewport, styles) {
  try {
    const tr = Util.transform(viewport.transform, item.transform);
    const fontHeight = Math.hypot(tr[2], tr[3]) || 0;
    if (!fontHeight) return null;

    const style = styles[item.fontName] || {};
    let ascent = fontHeight;
    if (typeof style.ascent === "number" && style.ascent > 0) {
      ascent = style.ascent * fontHeight;
    } else if (typeof style.descent === "number" && style.descent < 0) {
      // descent is negative fraction in many fonts
      ascent = (1 + style.descent) * fontHeight;
      if (ascent <= 0) ascent = fontHeight * 0.8;
    } else {
      ascent = fontHeight * 0.8;
    }

    const angle = Math.atan2(tr[1], tr[0]);
    let left;
    let top;
    if (Math.abs(angle) < 1e-5) {
      left = tr[4];
      top = tr[5] - ascent;
    } else {
      left = tr[4] + ascent * Math.sin(angle);
      top = tr[5] - ascent * Math.cos(angle);
    }

    const scaleX = Math.hypot(tr[0], tr[1]) || 1;
    // item.width is in glyph space along the text direction
    let wPx = (typeof item.width === "number" ? item.width : 0) * scaleX;
    if (!wPx || wPx < 0.5) {
      // fallback estimate from string length
      wPx = Math.max(strWidthFallback(item.str, fontHeight), fontHeight * 0.35);
    }

    // Slightly taller than ascent for easier clicking
    const hPx = Math.max(fontHeight, ascent) * 1.05;

    return { left, top, wPx, hPx, fontSize: fontHeight, angle };
  } catch {
    return null;
  }
}

function strWidthFallback(str, fontHeight) {
  // rough average glyph width
  return String(str).length * fontHeight * 0.5;
}

function mergeItemsIntoLines(items) {
  const sorted = [...items].sort((a, b) => {
    const dy = a.top - b.top;
    if (Math.abs(dy) > 0.5) return dy;
    return a.left - b.left;
  });

  const lines = [];
  let cur = null;

  const flush = () => {
    if (!cur) return;
    lines.push(finalizeLine(cur));
    cur = null;
  };

  for (const it of sorted) {
    // Skip pure whitespace fragments unless they help spacing inside a line
    const onlySpace = !it.str.trim();
    if (!cur) {
      if (onlySpace) continue;
      cur = newLine(it);
      if (it.hasEOL) flush();
      continue;
    }

    const curH = cur.bottom - cur.top;
    const midY = it.top + it.height / 2;
    const curMid = (cur.top + cur.bottom) / 2;
    const yTol = Math.max(curH, it.height) * 0.6;

    const sameLine = Math.abs(midY - curMid) <= yTol;
    if (!sameLine) {
      flush();
      if (onlySpace) continue;
      cur = newLine(it);
      if (it.hasEOL) flush();
      continue;
    }

    cur.items.push(it);
    cur.top = Math.min(cur.top, it.top);
    cur.bottom = Math.max(cur.bottom, it.top + it.height);
    cur.left = Math.min(cur.left, it.left);
    cur.right = Math.max(cur.right, it.left + it.width);
    if (it.hasEOL) flush();
  }
  flush();
  return lines.filter((l) => l.text.trim().length > 0);
}

function newLine(it) {
  return {
    items: [it],
    top: it.top,
    bottom: it.top + it.height,
    left: it.left,
    right: it.left + it.width,
  };
}

function finalizeLine(line) {
  const items = [...line.items].sort((a, b) => a.left - b.left);
  let text = "";

  for (let i = 0; i < items.length; i++) {
    const s = items[i].str;
    if (i === 0) {
      text = s;
      continue;
    }
    const prev = items[i - 1];
    const gap = items[i].left - (prev.left + prev.width);
    const spaceGate = Math.max(prev.fontSize * 0.18, 1.2);
    const needsSpace =
      gap > spaceGate && !/\s$/.test(text) && !/^\s/.test(s) && s.trim().length > 0;

    if (needsSpace) text += " ";
    text += s;
  }

  // Collapse weird multi-spaces from PDF extraction but keep single spaces
  text = text.replace(/[ \t\f\v]+/g, " ").replace(/\s+\n/g, "\n").trim();

  const fontSize =
    items.reduce((sum, it) => sum + it.fontSize, 0) / Math.max(items.length, 1);

  return {
    text,
    top: line.top,
    bottom: line.bottom,
    left: line.left,
    right: line.right,
    height: line.bottom - line.top,
    fontSize,
  };
}

/**
 * Optionally merge consecutive lines that belong to the same paragraph block
 * (similar font size + small vertical gap). Useful for multi-line edits.
 */
export function groupLinesIntoBlocks(lines, maxGapRatio = 0.85) {
  if (!lines.length) return [];
  const blocks = [];
  let cur = null;

  for (const line of lines) {
    if (!cur) {
      cur = { lines: [line], ...boxFromLine(line) };
      continue;
    }
    const prev = cur.lines[cur.lines.length - 1];
    const gap = line.y * 1000 - (prev.y + prev.h) * 1000; // relative-ish
    // compare using pixel boxes if available
    const gapPx =
      line.top != null && prev.top != null
        ? line.top - (prev.top + prev.height)
        : gap;
    const refH = prev.height || prev.h * 1000 || 14;
    const fontClose =
      Math.abs((line.fontSizeCss || 0) - (prev.fontSizeCss || 0)) <= refH * 0.35;
    const aligned =
      Math.abs((line.x || 0) - (prev.x || 0)) < 0.04 ||
      Math.abs((line.left || 0) - (prev.left || 0)) < refH;

    if (fontClose && aligned && gapPx >= -2 && gapPx <= refH * maxGapRatio) {
      cur.lines.push(line);
      expandBlock(cur, line);
    } else {
      blocks.push(finalizeBlock(cur));
      cur = { lines: [line], ...boxFromLine(line) };
    }
  }
  if (cur) blocks.push(finalizeBlock(cur));
  return blocks;
}

function boxFromLine(line) {
  return {
    left: line.left,
    top: line.top,
    right: line.left + line.width,
    bottom: line.top + line.height,
  };
}

function expandBlock(block, line) {
  block.left = Math.min(block.left, line.left);
  block.top = Math.min(block.top, line.top);
  block.right = Math.max(block.right, line.left + line.width);
  block.bottom = Math.max(block.bottom, line.top + line.height);
}

function finalizeBlock(block) {
  const sample = block.lines[0];
  const pageW = sample.w > 0 ? sample.width / sample.w : sample.width || 1;
  const pageH = sample.h > 0 ? sample.height / sample.h : sample.height || 1;
  const text = block.lines.map((l) => l.str).join("\n");
  const w = Math.max(block.right - block.left, 4);
  const h = Math.max(block.bottom - block.top, 4);
  return {
    id: `block-${sample.id}`,
    str: text,
    x: block.left / pageW,
    y: block.top / pageH,
    w: w / pageW,
    h: h / pageH,
    fontSizeCss: sample.fontSizeCss,
    left: block.left,
    top: block.top,
    width: w,
    height: h,
    lineCount: block.lines.length,
    lines: block.lines,
  };
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}
